/**
 * Servicio para manejar datos de equipación
 */

import api from './api';

class EquipacionService {
  
  /**
   * Obtener información de equipación desde el backend
   */
  static async getEquipacionInfo() {
    try {
      const response = await api.get('/postgres/auxiliar/equipacion');
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('No se pudo obtener información de equipación');
      }
    } catch (error) {
      console.error('❌ Error al obtener información de equipación:', error);
      
      // Fallback a valores por defecto si falla la API
      return {
        id: null,
        codigo: 'P999',
        nombre: 'Equipación Completa JOMA',
        descripcion: 'Incluye Camiseta y Pantalón Oficial de la Liga Prokids',
        precio: 15.00,
        moneda: 'EUR',
        marca: 'JOMA',
        articulos: ['Camiseta', 'Pantalón'],
        tallas: ['5XS-6XS', '4XS-3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL'],
        oficial: true,
        activo: true
      };
    }
  }

  /**
   * Validar si una talla es válida
   */
  static async validarTalla(talla) {
    try {
      const equipacion = await this.getEquipacionInfo();
      return equipacion.tallas.includes(talla);
    } catch (error) {
      console.error('❌ Error al validar talla:', error);
      return false;
    }
  }

  /**
   * Obtener todas las tallas disponibles
   */
  static async getTallasDisponibles() {
    try {
      const equipacion = await this.getEquipacionInfo();
      return equipacion.tallas;
    } catch (error) {
      console.error('❌ Error al obtener tallas disponibles:', error);
      // Fallback a tallas por defecto
      return ['5XS-6XS', '4XS-3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];
    }
  }

  /**
   * Formatear precio de equipación
   */
  static formatearPrecio(precio, moneda = 'EUR') {
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: moneda,
      }).format(precio);
    } catch (error) {
      return `${precio}€`;
    }
  }

  /**
   * Obtener texto descriptivo de la equipación
   */
  static async getTextoDescriptivo() {
    try {
      const equipacion = await this.getEquipacionInfo();
      return {
        titulo: `👕 ${equipacion.nombre} (+${this.formatearPrecio(equipacion.precio)})`,
        descripcion: equipacion.descripcion,
        marca: equipacion.marca,
        articulos: equipacion.articulos.join(' y '),
        precio: equipacion.precio,
        moneda: equipacion.moneda
      };
    } catch (error) {
      console.error('❌ Error al obtener texto descriptivo:', error);
      // Fallback
      return {
        titulo: '👕 Equipación Completa JOMA (+15€)',
        descripcion: 'Incluye Camiseta y Pantalón Oficial de la Liga Prokids',
        marca: 'JOMA',
        articulos: 'Camiseta y Pantalón',
        precio: 15.00,
        moneda: 'EUR'
      };
    }
  }

  /**
   * Cache para evitar múltiples llamadas a la API
   */
  static _cache = null;
  static _cacheTimestamp = null;
  static CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  /**
   * Obtener información de equipación con cache
   */
  static async getEquipacionInfoCached() {
    const now = Date.now();
    
    // Si tenemos cache válido, usarlo
    if (this._cache && this._cacheTimestamp && (now - this._cacheTimestamp) < this.CACHE_DURATION) {
      return this._cache;
    }

    // Obtener datos frescos
    const equipacion = await this.getEquipacionInfo();
    
    // Actualizar cache
    this._cache = equipacion;
    this._cacheTimestamp = now;
    
    return equipacion;
  }

  /**
   * Limpiar cache (útil cuando se actualiza la equipación)
   */
  static clearCache() {
    this._cache = null;
    this._cacheTimestamp = null;
  }

  /**
   * Obtener solicitudes de equipación (para administradores)
   */
  static async getSolicitudesEquipacion(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.talla) params.append('talla', filters.talla);
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.torneo) params.append('torneo', filters.torneo);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await api.get(`/equipacion/solicitudes?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener solicitudes de equipación:', error);
      throw error;
    }
  }

  /**
   * Obtener resumen de tallas (estadísticas)
   */
  static async getResumenTallas() {
    try {
      const response = await api.get('/equipacion/resumen-tallas');
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener resumen de tallas:', error);
      throw error;
    }
  }

  /**
   * Marcar equipación como entregada
   */
  static async marcarEquipacionEntregada(transactionId, entregada = true) {
    try {
      const response = await api.put(`/equipacion/entregar/${transactionId}`, {
        entregada
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al marcar equipación como entregada:', error);
      throw error;
    }
  }

  /**
   * Obtener equipación de un futbolista específico
   */
  static async getEquipacionFutbolista(futbolistaId) {
    try {
      const response = await api.get(`/equipacion/futbolista/${futbolistaId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener equipación del futbolista:', error);
      throw error;
    }
  }

  /**
   * Obtener valores únicos para filtros (torneos, tallas, estados)
   */
  static async getFiltrosEquipacion() {
    try {
      const response = await api.get('/equipacion/filtros');
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener filtros de equipación:', error);
      throw error;
    }
  }

  /**
   * Registrar equipación manual (pago en efectivo)
   */
  static async registrarEquipacionManual(data) {
    try {
      const response = await api.post('/equipacion/registrar-manual', data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al registrar equipación manual:', error);
      throw error;
    }
  }
}

export default EquipacionService;
